import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import {
    CartLineModel,
    CartModel,
    OrderLineModel,
    ProductDto,
    ProductModel,
    ProductPriceDto,
    QuoteLineModel,
    WishListLineModel,
} from "@insite/client-framework/Types/ApiModels";
import { ProductCategoriesModel } from "@tggstore/Services/CommonServices";

interface GA4Event {
    event: string;
    ecommerce: GA4Ecommerce;
    search_term?: string;
    user_id?: string;
}

interface GA4Ecommerce {
    account_number: string;
    currency?: string;
    value?: number;
    items: GA4EcommerceItem[];
    affiliation?: string;
    productCategories?: ProductCategoriesModel[];
    transaction_id?: string;
    transaction_type?: string;
    tax?: number;
    shipping?: number;
    indexes?: any;
    productsPricingPrices?: ProductPriceDto[];
    item_list_id?: string;
    item_list_name?: string;
    search_term?: string;
    user_id?: string;
    a2c_method?: string; // Should be dynamic and should indicate where the add_to_cart event took place.
    promo_added?: string; // boolean value (true/false)
    promo_code?: string; // value of promo code string.
    file_name?: string;
    file_extension?: string;
    // add more if needed
}

interface GA4EcommerceItem {
    item_id: string;
    item_name: string;
    affiliation: string;
    discount: number;
    index: number;
    item_brand: string;
    item_category: string;
    item_category2?: string;
    item_category3?: string;
    item_variant: string;
    price: number;
    quantity: number;
    // add more if needed
}

type KeyPair<K extends string, V> = {
    [key in K]: V;
};

// OOTB from file /modules/client-framework/src/Common/Utilities/tracking.ts
// get global for google tag manager
function getDataLayer() {
    return (window as any).dataLayer;
}

function setData(eventData: any) {
    const dataLayer = getDataLayer();
    if (dataLayer) {
        dataLayer.push(eventData);
    } else {
        (window as any).dataLayer = [eventData];
    }
}
// Helper function to calculate discount
function calculateDiscount(pricing: CartLineModel["pricing"]) {
    if (pricing?.regularPrice && pricing?.actualPrice) {
        return Math.max(0, pricing.regularPrice - pricing.actualPrice);
    }
    return 0;
}

// cartlines is either a QuoteLineModel[], a CartLineModel[] or a ProductModel (or keep adding more types)
export const setGA4Event = (
    cartLines:
        | QuoteLineModel[]
        | CartLineModel[]
        | CartModel
        | ProductDto[]
        | ProductModel[]
        | OrderLineModel[]
        | WishListLineModel[]
        | Array<{ product: ProductModel; productInfo: Omit<ProductInfo, "productDetailPath"> }>
        | Array<{ product: CartLineModel; productInfo: Omit<ProductInfo, "productDetailPath"> }>
        | [],
    eventName: string,
    additionalInfo?: Partial<GA4Ecommerce>,
) => {
    if (IS_SERVER_SIDE) {
        return;
    }

    let eventData = {
        event: eventName,
        ecommerce: {
            account_number: additionalInfo?.account_number ?? "",
            currency: additionalInfo?.currency ?? "",
            value: additionalInfo?.value ?? 0, // total value of transaction, including all price * quantity of products, excluding sales tax and shipping
            a2c_method: additionalInfo?.a2c_method ?? "",
            // TODO: Add promotions tracking
            // promo_added: additionalInfo?.promo_added ?? false,
            // promo_code: additionalInfo?.promo_code ?? "",
            items: [],
        },
    } as GA4Event;

    if (eventName === "purchase") {
        eventData.ecommerce.transaction_id = additionalInfo?.transaction_id ?? "";
        eventData.ecommerce.transaction_type = additionalInfo?.transaction_type ?? "";
        eventData.ecommerce.tax = additionalInfo?.tax ?? 0;
        eventData.ecommerce.shipping = additionalInfo?.shipping ?? 0;
    }

    if (["view_cart", "begin_checkout", "purchase"].includes(eventName)) {
        const items = cartLines as CartLineModel[];
        const itemCategories = additionalInfo?.productCategories ?? [];

        // Create a map for faster category lookups
        const categoryMap = new Map(
            itemCategories.map(category => [
                category.erpNumber,
                {
                    categories: category.categories,
                    attributes: category.attributes,
                },
            ]),
        );

        eventData.ecommerce.items = items.map((item, index) => {
            const productData = categoryMap.get(item.erpNumber);
            const productCategories = productData?.categories;
            const productAttributesManufacturer = productData?.attributes["manufacturer Name"] ?? "";
            const productAttributesColor = productData?.attributes["color/Finish"] ?? "";

            return {
                item_id: item.erpNumber ?? "",
                item_name: item.shortDescription ?? "",
                item_brand: productAttributesManufacturer,
                price: item.pricing?.actualPrice ?? 0,
                quantity: item.qtyOrdered ?? 0,
                discount: calculateDiscount(item.pricing),
                item_category: productCategories?.item_category ?? "",
                item_category2: productCategories?.item_category2 ?? "",
                item_category3: productCategories?.item_category3 ?? "",
                item_variant: productAttributesColor,
                affiliation: additionalInfo?.affiliation ?? "",
                index,
            } as GA4EcommerceItem;
        });
    }

    if (eventName === "view_item_list") {
        eventData = {
            event: eventName,
            ecommerce: {
                account_number: additionalInfo?.account_number ?? "",
                item_list_id: additionalInfo?.item_list_id ?? "",
                item_list_name: additionalInfo?.item_list_name ?? "", // is sent empty for the moment, BE to provide a custom api for the categories
                items: [],
            },
        } as GA4Event;

        const ga4Items = cartLines as ProductModel[];
        const { indexes, productsPricingPrices, affiliation } = additionalInfo ?? {};
        const itemCategories = additionalInfo?.productCategories ?? [];

        // Create a map for faster category lookups
        const categoryMap = new Map(
            itemCategories.map(category => [
                category.erpNumber,
                {
                    categories: category.categories,
                    attributes: category.attributes,
                },
            ]),
        );

        ga4Items.forEach(item => {
            // Find the product categories for this item
            const productData = categoryMap.get(item.productNumber);
            const productCategories = productData?.categories;
            const productAttributesManufacturer = productData?.attributes["manufacturer Name"] ?? "";
            const productAttributesColor = productData?.attributes["color/Finish"] ?? "";

            const productInfo = createFromProduct(item);
            productInfo.pricing = (productsPricingPrices ?? []).find(
                (x: { productId: any }) => x.productId === item.id,
            );

            const index = indexes.find((x: { id: string }) => x.id === item.id)?.attribute;
            const itemData = {
                item_id: item.productNumber ?? "",
                item_name: item.productTitle ? `${item.productTitle}` : "",
                affiliation: affiliation ?? "", // warehouse/store/location
                discount: productInfo.pricing ? calculateDiscount(productInfo.pricing) : 0, // amount of discount from coupon, if applicable
                index: Number(index),
                item_brand: productAttributesManufacturer,
                item_category: productCategories?.item_category ?? "",
                item_category2: productCategories?.item_category2 ?? "",
                item_category3: productCategories?.item_category3 ?? "",
                item_variant: productAttributesColor,
                item_list_name: productCategories?.item_category3
                    ? productCategories?.item_category3
                    : productCategories?.item_category2
                    ? productCategories?.item_category2
                    : "", // is sent empty for the moment
                price: productInfo.pricing?.actualPrice ?? 0,
                quantity: productInfo.qtyOrdered,
            } as GA4EcommerceItem;

            eventData.ecommerce.items.push(itemData);
        });
    }

    if (eventName === "add_to_cart") {
        const items = cartLines as CartLineModel[];
        const itemCategories = additionalInfo?.productCategories ?? [];
        const { indexes } = additionalInfo ?? {};

        const categoryMap = new Map(
            itemCategories.map(category => [
                category.erpNumber,
                {
                    categories: category.categories,
                    attributes: category.attributes,
                },
            ]),
        );

        let count = 0;

        items.forEach(item => {
            // Find the product categories for this item
            const productData = categoryMap.get(item.erpNumber);
            const productCategories = productData?.categories;
            const productAttributesManufacturer = productData?.attributes["manufacturer Name"] ?? "";
            const productAttributesColor = productData?.attributes["color/Finish"] ?? "";

            let cartIndex = 0;
            const ga4ArrayIndex = localStorage.getItem("ga4ArrayFilter");

            if (indexes && indexes.length > 0) {
                cartIndex = indexes.find((x: { id: string }) => x.id === item.productId)?.attribute;
            } else if (ga4ArrayIndex) {
                try {
                    const productIds = JSON.parse(ga4ArrayIndex);
                    cartIndex = productIds.indexOf(item.productId);
                } catch {
                    cartIndex = 0;
                }

                if (cartIndex === -1) {
                    cartIndex = count;
                }
            }

            const itemData = {
                item_id: item.erpNumber ?? "",
                item_name: item.shortDescription ?? "",
                affiliation: additionalInfo?.affiliation ?? "",
                discount: calculateDiscount(item.pricing),
                index: Number(cartIndex),
                item_brand: productAttributesManufacturer,
                item_category: productCategories?.item_category ?? "",
                item_category2: productCategories?.item_category2 ?? "",
                item_category3: productCategories?.item_category3 ?? "",
                item_variant: productAttributesColor,
                price: item.pricing?.actualPrice ?? 0,
                quantity: item.qtyOrdered ?? 0,
            } as GA4EcommerceItem;

            eventData.ecommerce.items.push(itemData);
            count++;
        });
    }

    if (eventName === "add_to_wishlist") {
        const items = cartLines as any;
        let count = 0;
        const { affiliation, indexes } = additionalInfo ?? {};
        const itemCategories = additionalInfo?.productCategories ?? [];

        const categoryMap = new Map(
            itemCategories.map(category => [
                category.erpNumber,
                {
                    categories: category.categories,
                    attributes: category.attributes,
                },
            ]),
        );

        items.forEach((item: any) => {
            const productInfo = item.productInfo;
            const product = item.product; // Find the product categories for this item
            const productData = categoryMap.get(
                product != null
                    ? typeof product.productNumber !== "undefined"
                        ? product.productNumber
                        : product.erpNumber
                    : item.productErpNumber,
            );
            const productCategories = productData?.categories;
            const productAttributesManufacturer = productData?.attributes["manufacturer Name"] ?? "";
            const productAttributesColor = productData?.attributes["color/Finish"] ?? "";
            const indexList =
                indexes != null ? indexes.find((x: { id: string }) => x.id === product.id)?.attribute : null;

            const itemData = {
                item_id:
                    product != null
                        ? typeof product.productNumber !== "undefined"
                            ? product.productNumber
                            : product.erpNumber
                        : item.productErpNumber,
                item_name:
                    product != null
                        ? typeof product.productTitle !== "undefined"
                            ? product.productTitle
                            : product.shortDescription
                        : item.shortDescription,
                affiliation: affiliation ?? "", // warehouse/store/location
                discount: product != null ? (productInfo.pricing ? calculateDiscount(productInfo.pricing) : 0) : 0, // amount of discount from coupon, if applicable
                index: product
                    ? indexList
                        ? Number(indexList)
                        : product.line
                        ? product.line
                        : Number(count)
                    : item.lineNumber,
                item_brand: productAttributesManufacturer,
                item_category: productCategories?.item_category ?? "",
                item_category2: productCategories?.item_category2 ?? "",
                item_category3: productCategories?.item_category3 ?? "",
                item_list_name: productCategories?.item_category3
                    ? productCategories?.item_category3
                    : productCategories?.item_category2
                    ? productCategories?.item_category2
                    : "", // is sent empty for the moment
                item_variant: productAttributesColor,
                price: productInfo != null ? productInfo.pricing?.actualPrice ?? 0 : item.unitNetPrice,
                quantity: productInfo != null ? productInfo.qtyOrdered : item.qtyOrdered,
            } as GA4EcommerceItem;

            eventData.ecommerce.items.push(itemData);
            count++;
        });
    }

    if (eventName === "search_results") {
        eventData = {
            event: eventName,
            search_term: additionalInfo?.search_term ?? "",
        } as GA4Event;
    }

    if (eventName === "none") {
        eventData = {
            user_id: additionalInfo?.user_id ?? "",
        } as GA4Event;
    }

    if (eventName === "login") {
        eventData = {
            event: eventName,
            user_id: additionalInfo?.user_id ?? "",
        } as GA4Event;
    }

    if (eventName === "view_item") {
        const ga4Items = cartLines as ProductModel[];
        const itemCategories = additionalInfo?.productCategories ?? [];
        const { productsPricingPrices, affiliation } = additionalInfo ?? {};

        const categoryMap = new Map(
            itemCategories.map(category => [
                category.erpNumber,
                {
                    categories: category.categories,
                    attributes: category.attributes,
                },
            ]),
        );

        ga4Items.forEach(item => {
            // Find the product categories for this item
            const productData = categoryMap.get(item.productNumber);
            const productCategories = productData?.categories;
            const productAttributesManufacturer =
                typeof productData?.attributes !== "undefined"
                    ? productData?.attributes["manufacturer Name"] ?? ""
                    : "";
            const productAttributesColor =
                typeof productData?.attributes !== "undefined" ? productData?.attributes["color/Finish"] ?? "" : "";

            const productInfo = createFromProduct(item);
            productInfo.pricing = (productsPricingPrices ?? []).find(
                (x: { productId: any }) => x.productId === item.id,
            );

            const itemData = {
                item_id: item.productNumber ?? "",
                item_name: item.productTitle ? `${item.productTitle}` : "",
                affiliation: affiliation ?? "", // warehouse/store/location
                discount: productInfo.pricing ? calculateDiscount(productInfo.pricing) : 0, // amount of discount from coupon, if applicable
                index: 0,
                item_brand: productAttributesManufacturer,
                item_category: productCategories?.item_category ?? "",
                item_category2: productCategories?.item_category2 ?? "",
                item_category3: productCategories?.item_category3 ?? "",
                item_list_name: productCategories?.item_category3
                    ? productCategories?.item_category3
                    : productCategories?.item_category2
                    ? productCategories?.item_category2
                    : "", // is sent empty for the moment
                item_variant: productAttributesColor,
                price: productInfo.pricing?.actualPrice ?? 0,
                quantity: productInfo.qtyOrdered,
            } as GA4EcommerceItem;

            eventData.ecommerce.items.push(itemData);
        });
    }

    if (eventName === "file_download") {
        eventData = {
            event: eventName,
            file_name: additionalInfo?.file_name ?? "",
            file_extension: additionalInfo?.file_extension ?? "",
        } as any;
    }

    // Verndale Customization
    localStorage.removeItem("ga4Array");
    // End Verndale Customization

    setData(eventData);
};
